import { type CSSProperties, type ReactNode } from "react";
import { Bell, Menu, MessageSquare } from "lucide-react";

import { SidebarProvider, useSidebar } from "@/shared/components/ui/sidebar";
import { useDemoMode } from "@/pages/demo/DemoModeContext";
import { useLawyerPersons } from "@/shared/hooks/useLawyerEstates";
import { AlertsPanel } from "@/pages/lawyer/dashboard/components/AlertsPanel";
import { SupportPanel } from "@/pages/lawyer/dashboard/components/SupportPanel";
import { LawyerSidebar } from "./components/nav/LawyerSidebar";

interface LawyerLayoutProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  titleAction?: ReactNode;
}

function LawyerLayoutInner({ children, title, subtitle, titleAction }: LawyerLayoutProps) {
  const { isMobile, toggleSidebar } = useSidebar();
  const { isDemoMode } = useDemoMode();
  const { data: estates } = useLawyerPersons();

  const actionCount = estates?.filter((e) => e.highlight?.category === "action").length ?? 0;

  return (
    <div
      className={`${isDemoMode ? "flex-1" : "h-screen"} w-full max-w-full flex overflow-hidden bg-subtle`}
    >
      <LawyerSidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="flex-shrink-0 bg-white border-y border-primary">
          <div className="flex items-center justify-between px-4 py-4">
            {isMobile ? (
              <button
                onClick={toggleSidebar}
                className="flex items-center justify-center size-8 rounded-md hover:bg-subtle transition-colors"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5 text-primary" />
              </button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-4">
              <AlertsPanel estates={estates ?? []}>
                <button
                  className="flex items-center gap-2 rounded-full border border-primary bg-white px-4 py-1 text-sm text-primary hover:bg-subtle transition-colors"
                  aria-label="Alerts"
                >
                  <Bell className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">Alerts</span>
                  {actionCount > 0 && (
                    <span className="flex size-4 items-center justify-center rounded-full bg-brand text-[9px] font-medium text-white">
                      {actionCount}
                    </span>
                  )}
                </button>
              </AlertsPanel>

              <SupportPanel>
                <button
                  className="flex items-center gap-2 rounded-full border border-primary bg-white px-4 py-1 text-sm text-primary hover:bg-subtle transition-colors"
                  aria-label="Get support"
                >
                  <MessageSquare className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">Get support</span>
                </button>
              </SupportPanel>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-8">
            {title && (
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex flex-col gap-2">
                  <h2>{title}</h2>
                  {subtitle && <p className="text-secondary">{subtitle}</p>}
                </div>
                {titleAction}
              </div>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export function LawyerLayout(props: LawyerLayoutProps) {
  const { isDemoMode } = useDemoMode();
  return (
    <SidebarProvider
      style={{ "--sidebar-width": "250px" } as CSSProperties}
      className={isDemoMode ? "min-h-0 flex-1" : undefined}
    >
      <LawyerLayoutInner {...props} />
    </SidebarProvider>
  );
}
